import React, { memo, useCallback, useMemo } from 'react';
import { View, Platform } from 'react-native';
import { format, parseISO } from 'date-fns';
import Text from 'design-system/Text';
import Button from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import Section from 'design-system/Section';
import Indicator from 'design-system/Indicator';
import { CardView } from 'design-system/CardView';
import IconPlus from 'design-system/icons/IconPlus';
import IconCircle from 'design-system/icons/IconCircle';
import SectionHeader from 'design-system/SectionHeader';
import { SettingsItem } from 'design-system/SettingsItem';
import { TextButton } from 'design-system/TextButton';
import TouchableBounceHighlight from 'design-system/TouchableBounceHighlight';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useSubscriptionScreenContext } from 'features/subscriptions/context/subscriptionScreenContext';
import { Separator } from 'design-system';
import { isWeb } from '../../../../constants';
import RowSubscription from '../row/RowSubscription';
const strings = {
    whatHappened: (dueDate) => `This payment was due on ${dueDate} but hasn't shown up yet. What happened?`,
    cancelled: "I've cancelled this",
    skipped: "I'm paying later",
    alreadyPaid: "I've already paid",
    income: {
        whatHappened: "We haven't spotted this income in your account yet. Did something change?",
        cancelled: 'This income has stopped',
        skipped: "It's just delayed",
        alreadyPaid: "It's already arrived",
    },
};
const SubscriptionPrediction = ({ date, onDone, onEdit, onPress, onAdd, onCancel, onSkip, onAlreadyPaid, isCancelling, subscriptionId, subscription, noMarginHorizontal, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { editing, hasData, isFetching, hideAddMoreTxnsButton } = useSubscriptionScreenContext();
    const navigation = useMainStackNavigation();
    const addMoreTxns = useCallback(() => {
        navigation.push('SubscriptionsSearch', {
            subscriptionId,
            income: subscription.isIncome,
            empty: {
                title: 'Add a transaction',
                subtitle: 'Search through your transactions and select the one you want to include.\nIt works like magic!',
            },
        });
    }, [subscriptionId, subscription.isIncome]);
    const isMissed = !!subscription.missedPayment && !!(onCancel || onSkip || onAlreadyPaid);
    const missedActions = useMemo(() => {
        if (!isMissed)
            return null;
        const isIncome = subscription.isIncome === true;
        const dueDate = subscription.userPrediction?.date ?? subscription.predictions?.[0]?.date;
        let whatHappened = null;
        if (isIncome) {
            whatHappened = strings.income.whatHappened;
        }
        else if (dueDate) {
            whatHappened = strings.whatHappened(format(parseISO(dueDate), 'd MMMM'));
        }
        return (<View style={styles.missedActions}>
        <Separator style={styles.separatorPadding}/>
        {whatHappened ? (<Text TextThin-14 Gray style={styles.missingTextPadding}>
            {whatHappened}
          </Text>) : null}
        <View style={styles.buttonContainer}>
          {onCancel ? (<Button errorReversed square title={isIncome ? strings.income.cancelled : strings.cancelled} isFetching={isCancelling} onPress={onCancel}/>) : null}
          {onSkip ? (<Button errorReversed square title={isIncome ? strings.income.skipped : strings.skipped} onPress={onSkip}/>) : null}
          {onAlreadyPaid ? (<Button errorReversed square title={isIncome ? strings.income.alreadyPaid : strings.alreadyPaid} onPress={onAlreadyPaid}/>) : null}
        </View>
      </View>);
    }, [
        isMissed,
        subscription.isIncome,
        subscription.userPrediction?.date,
        subscription.predictions,
        styles.missedActions,
        styles.separatorPadding,
        styles.missingTextPadding,
        styles.buttonContainer,
        onCancel,
        onSkip,
        onAlreadyPaid,
        isCancelling,
    ]);
    const renderNextPayment = useMemo(() => {
        let handlePress = date ? onPress : onAdd;
        if (isMissed)
            handlePress = undefined;
        const card = (<CardView noMarginTop noMarginBottom style={[
                styles.cardView,
                noMarginHorizontal ? styles.noMarginHorizontal : null,
            ]}>
        {date ? (<RowSubscription data={subscription} isNextPaymentRow hideDuplicateLabel onPress={handlePress && isWeb ? handlePress : undefined}/>) : (<SettingsItem onPress={handlePress && isWeb ? handlePress : undefined} title="Add prediction" renderIcon={() => (<View style={styles.iconContainer}>
                <IconCircle width={44} height={44}>
                  <IconPlus color={colors.text.brand}/>
                </IconCircle>
              </View>)}/>)}
        {missedActions}
      </CardView>);
        return (<Section title="Next payment" style={styles.cardSectionHeader} titleContainerStyle={noMarginHorizontal ? styles.noMarginHorizontal : null}>
        {handlePress && !isWeb ? (<TouchableBounceHighlight onPress={handlePress}>
            {card}
          </TouchableBounceHighlight>) : (card)}
      </Section>);
    }, [
        date,
        isMissed,
        missedActions,
        noMarginHorizontal,
        onPress,
        onAdd,
        colors.text.brand,
        styles.cardSectionHeader,
        styles.cardView,
        styles.noMarginHorizontal,
        styles.iconContainer,
        subscription,
    ]);
    const pastSectionHeader = useMemo(() => isFetching && !hasData ? (<View style={styles.indicatorContainer}>
          <Indicator dark/>
        </View>) : (<>
          <SectionHeader horizontalMargins style={[
            styles.cardSectionHeader,
            noMarginHorizontal ? styles.noMarginHorizontal : null,
        ]} button={!editing && !hasData ? null : (<TextButton text={editing ? 'Done' : 'Edit'} onPress={editing ? onDone : onEdit}/>)}>
            Past payments
          </SectionHeader>
          <Spacer.H16 />
          {!editing && !hideAddMoreTxnsButton && (<View style={[styles.addMoreTxn, !hasData && styles.completeAddMoreTxn]}>
              <SettingsItem onPress={addMoreTxns} title="Add more transactions" renderIcon={() => (<View style={styles.iconContainer}>
                    <IconCircle width={44} height={44}>
                      <IconPlus color={colors.text.brand}/>
                    </IconCircle>
                  </View>)}/>
            </View>)}
        </>), [
        isFetching,
        hasData,
        styles.indicatorContainer,
        styles.cardSectionHeader,
        styles.noMarginHorizontal,
        styles.addMoreTxn,
        styles.completeAddMoreTxn,
        styles.iconContainer,
        noMarginHorizontal,
        editing,
        onDone,
        onEdit,
        hideAddMoreTxnsButton,
        addMoreTxns,
        colors.text.brand,
    ]);
    return (<>
      {renderNextPayment}
      {pastSectionHeader}
    </>);
};
const styleSet = createStyleSheets((colors) => ({
    sectionHeader: {
        padding: rem(16),
        flexDirection: 'row',
        justifyContent: 'center',
    },
    textContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    amountContainer: {
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
    accountRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    accountIcon: {
        width: rem(16),
        height: rem(16),
        marginEnd: rem(4),
        borderRadius: rem(8),
    },
    addMoreTxn: {
        marginHorizontal: rem(16),
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(Platform.OS !== 'android' && {
            overflow: 'hidden',
        }),
        ...(isWeb && {
            paddingTop: rem(8),
            paddingHorizontal: rem(8),
        }),
    },
    completeAddMoreTxn: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && {
            paddingBottom: rem(8),
        }),
    },
    cardSectionHeader: {
        marginTop: rem(24),
        marginBottom: 0,
    },
    indicatorContainer: {
        marginTop: rem(16),
        alignItems: 'center',
    },
    cardView: {
        ...(isWeb && {
            padding: rem(8),
        }),
    },
    missedActions: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(16),
    },
    noMarginHorizontal: {
        marginHorizontal: 0,
    },
    iconContainer: {
        marginRight: rem(16),
    },
    buttonContainer: {
        paddingTop: rem(8),
        gap: rem(12),
    },
    missingTextPadding: {
        paddingVertical: rem(8),
    },
    separatorPadding: {
        marginHorizontal: -rem(16),
    },
}));
export default memo(SubscriptionPrediction);
